import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { NotificationsService } from "angular2-notifications";
import { ConfigService } from "@app/app-config.service";
import { Router } from "@angular/router";
import { Observable, throwError } from "rxjs";
import * as status_code from "../../../../../status.json";
import { capitalize } from "@shared/utility";
import { ToastrService } from "ngx-toastr";
import { CommonService } from "../../utility/common.service";

@Injectable()
export class HttpResponseHandler {
  blockToastFor = ["s_407"];

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private notificationsService: NotificationsService,
    private configService: ConfigService,
    private toastr: ToastrService,
    private commonService: CommonService
  ) {
    // console.log('Response handler-------------------');
  }

  /**
   * Global http error handler.
   *
   * @param error
   * @param source
   * @returns {ErrorObservable}
   */
  public onCatch(response: any, source: Observable<any>): Observable<any> {
    // console.log('Response handler-------------------', response.status);
    // console.log('Response handler-------------------',source);
    switch (response.status) {
      case 400:
        this.handleBadRequest(response);
        break;

      case 401:
        this.handleUnauthorized(response);
        break;

      case 403:
        this.handleForbidden();
        break;

      case 404:
        this.handleNotFound(response);
        break;

      case 500:
        this.handleServerError();
        break;

      default:
        break;
    }

    return throwError(response);
  }
  public onSuccess(response: any, url: any): Observable<any> {
    // console.log("Response Holder----------", response);
    switch (response.status_code) {
      case response.status_code:
        this.handleAPIResponse(response, url);
        break;

      default:
        break;
    }

    return throwError(response);
  }
  private handleAPIResponse(response: any, url: any): void {
    // console.log("Response Holder----------", response);
    if (!response) {
      return;
    }
    if (response && response.messagedetails && response.messagedetails.ispopup) {
      console.log(response);
      if (response && response.status) {
        this.toastr.success(
          this.commonService.capitalize(
            response.messagedetails.message
          ),
          "",
          this.configService.get("notifications").options
        );
      } else {
        this.toastr.warning(
          this.commonService.capitalize(
            response.messagedetails.message
          ),
          "",
          this.configService.get("notifications").options
        );

        if (response.status_code == "s_401") {
          // console.log("User Master Dashboard Logout()");
          sessionStorage.setItem("flag", "true");
          localStorage.removeItem("currentUser");
          localStorage.removeItem("username");
          this.router.navigate(["/login"]);
        }
      }
    }
  }

  /**
   * Shows notification errors when server response status is 401
   *
   * @param error
   */
  private handleBadRequest(responseBody: any): void {
    if (responseBody._body) {
      try {
        const bodyParsed = responseBody.json();
        this.handleErrorMessages(bodyParsed);
      } catch (error) {
        this.handleServerError();
      }
    } else {
      this.handleServerError();
    }
  }

  /**
   * Shows notification errors when server response status is 401 and redirects user to login page
   *
   * @param responseBody
   */
  private handleUnauthorized(responseBody: any): void {
    // Read configuration in order to see if we need to display 401 notification message
    let unauthorizedEndpoints: Array<string> = this.configService.get(
      "notifications"
    ).unauthorizedEndpoints;

    unauthorizedEndpoints = unauthorizedEndpoints.filter(
      (endpoint) => this.getRelativeUrl(responseBody.url) === endpoint
    );
    this.router.navigate(["/login"]);

    if (unauthorizedEndpoints.length) {
      this.notificationsService.info(
        "Info",
        this.translateService.instant("ServerError401"),
        this.configService.get("notifications").options
      );
    }
  }

  /**
   * Shows notification errors when server response status is 403
   */
  private handleForbidden(): void {
    this.notificationsService.error(
      "error",
      this.translateService.instant("ServerError403"),
      this.configService.get("notifications").options
    );
    this.router.navigate(["/login"]);
  }

  /**
   * Shows notification errors when server response status is 404
   *
   * @param responseBody
   */
  private handleNotFound(responseBody: any): void {
    // Read configuration in order to see if we need to display 401 notification message
    let notFoundEndpoints: Array<string> = this.configService.get(
      "notifications"
    ).notFoundEndpoints;
    notFoundEndpoints = notFoundEndpoints.filter(
      (endpoint) => this.getRelativeUrl(responseBody.url) === endpoint
    );

    if (notFoundEndpoints.length) {
      const message = this.translateService.instant("ServerError404"),
        title = this.translateService.instant("ErrorNotificationTitle");

      this.showNotificationError(title, message);
    }
  }

  /**
   * Shows notification errors when server response status is 500
   */
  private handleServerError(): void {
    const message = this.translateService.instant("ServerError500"),
      title = this.translateService.instant("ErrorNotificationTitle");

    this.showNotificationError(title, message);
  }

  /**
   * Parses server response and shows notification errors with translated messages
   *
   * @param response
   */
  private handleErrorMessages(response: any): void {
    if (!response) {
      return;
    }

    for (const key of Object.keys(response)) {
      if (Array.isArray(response[key])) {
        response[key].forEach((value) =>
          this.showNotificationError("Info", this.getTranslatedValue(value))
        );
      } else {
        this.showNotificationError(
          "Info",
          this.getTranslatedValue(response[key])
        );
      }
    }
  }

  /**
   * Extracts and returns translated value from server response
   *
   * @param value
   * @returns {string}
   */
  private getTranslatedValue(value: string): string {
    if (value.indexOf("[") > -1) {
      const key = value.substring(
        value.lastIndexOf("[") + 1,
        value.lastIndexOf("]")
      );
      value = this.translateService.instant(key);
    }

    return value;
  }

  /**
   * Returns relative url from the absolute path
   *
   * @param responseBody
   * @returns {string}
   */
  private getRelativeUrl(url: string): string {
    return url ? url.toLowerCase().replace(/^(?:\/\/|[^\/]+)*\//, "") : '';
  }

  /**
   * Shows error notification with given title and message
   *
   * @param title
   * @param message
   */
  private showNotificationError(title: string, message: string): void {
    this.notificationsService.error(
      title,
      message,
      this.configService.get("notifications").options
    );
  }
}
